import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.map.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.promise.finally.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.regexp.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { createPostMessage } from '../utils/protocol';
import { generateRequestId } from '../utils/id';
import { isFunction } from '../utils/is';
import { MessageType, Messages, StreamType as StreamTypeConstant, StreamState as StreamStateConstant, StreamMode as StreamModeConstant, MessageRole, formatMessage, DefaultTimeout, StreamInternalMessageType, StreamEvent, ErrorCode } from '../constants';
import { IframeStreamCore } from './stream-core';
import { RequestIframeStreamError } from './error';

/**
 * Generate a unique stream ID
 */
function generateStreamId() {
  return `stream_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * IframeWritableStream - Server-side writable stream
 *
 * Writer/producer stream.
 * It can be created on either side:
 * - Server → Client: used as response stream (via `res.sendStream(stream)`)
 * - Client → Server: used as request body stream (via `client.sendStream(path, stream)`)
 */
export class IframeWritableStream extends IframeStreamCore {
  constructor(options = {}) {
    var _options$type, _options$chunked, _options$mode, _options$expireTimeou;
    var streamId = generateStreamId();
    var streamType = (_options$type = options.type) !== null && _options$type !== void 0 ? _options$type : StreamTypeConstant.DATA;
    var chunked = (_options$chunked = options.chunked) !== null && _options$chunked !== void 0 ? _options$chunked : true;
    super(streamId, streamType, chunked, options.metadata, false, options.mode);
    _defineProperty(this, "context", null);
    _defineProperty(this, "expireTimer", null);
    _defineProperty(this, "idleTimer", null);
    _defineProperty(this, "lastRemoteActivityAt", Date.now());
    _defineProperty(this, "heartbeatInFlight", null);
    /** pull/ack protocol */
    _defineProperty(this, "pullCredit", 0);
    _defineProperty(this, "seq", 0);
    _defineProperty(this, "pendingBytes", 0);
    _defineProperty(this, "pendingQueue", []);
    _defineProperty(this, "pumping", false);
    _defineProperty(this, "completionPromise", null);
    _defineProperty(this, "resolveCompletion", null);
    _defineProperty(this, "rejectCompletion", null);
    _defineProperty(this, "ackWaiters", new Map());
    _defineProperty(this, "ackReceiverRegistered", false);
    this.mode = (_options$mode = options.mode) !== null && _options$mode !== void 0 ? _options$mode : StreamModeConstant.PULL;
    this.iterator = options.iterator;
    this.nextFn = options.next;
    this.autoResolve = options.autoResolve;
    // Default to async-timeout length to avoid leaking long-lived streams
    this.expireTimeout = (_options$expireTimeou = options.expireTimeout) !== null && _options$expireTimeou !== void 0 ? _options$expireTimeou : DefaultTimeout.ASYNC;
    this.streamTimeout = options.streamTimeout;
    this.maxPendingChunks = options.maxPendingChunks;
    this.maxPendingBytes = options.maxPendingBytes;
  }
  enqueue(item) {
    var max = this.maxPendingChunks;
    if (typeof max === 'number' && max > 0 && this.pendingQueue.length >= max) {
      var _this$context;
      throw new RequestIframeStreamError({
        message: formatMessage(Messages.STREAM_PENDING_QUEUE_OVERFLOW, max),
        code: ErrorCode.STREAM_PENDING_QUEUE_OVERFLOW,
        streamId: this.streamId,
        requestId: (_this$context = this.context) === null || _this$context === void 0 ? void 0 : _this$context.requestId
      });
    }
    var bytes = this.estimateChunkBytes(item.data);
    var maxBytes = this.maxPendingBytes;
    if (typeof maxBytes === 'number' && maxBytes > 0) {
      var next = this.pendingBytes + bytes;
      if (!Number.isFinite(next) || next > maxBytes) {
        var _this$context2;
        throw new RequestIframeStreamError({
          message: formatMessage(Messages.STREAM_PENDING_BYTES_OVERFLOW, maxBytes),
          code: ErrorCode.STREAM_PENDING_BYTES_OVERFLOW,
          streamId: this.streamId,
          requestId: (_this$context2 = this.context) === null || _this$context2 === void 0 ? void 0 : _this$context2.requestId
        });
      }
    }
    this.pendingQueue.push(_objectSpread(_objectSpread({}, item), {}, {
      bytes
    }));
    this.pendingBytes += bytes;
  }
  estimateChunkBytes(data) {
    if (data === null || data === undefined) return 0;
    if (typeof data === 'string') return this.utf8ByteLength(data);
    try {
      // ArrayBuffer
      if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
        return data.byteLength;
      }
      // TypedArray / DataView
      if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView && ArrayBuffer.isView(data)) {
        return data.byteLength;
      }
    } catch (_unused) {
      /** ignore */
    }
    try {
      // Blob / File
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        return data.size;
      }
    } catch (_unused2) {
      /** ignore */
    }

    // Strategy C: only count well-defined types; other values are not counted.
    return 0;
  }
  utf8ByteLength(text) {
    try {
      if (typeof TextEncoder !== 'undefined') {
        return new TextEncoder().encode(text).length;
      }
    } catch (_unused3) {
      /** ignore */
    }
    try {
      // eslint-disable-next-line no-undef
      if (typeof Buffer !== 'undefined') {
        // eslint-disable-next-line no-undef
        return Buffer.byteLength(text, 'utf8');
      }
    } catch (_unused4) {
      /** ignore */
    }
    return text.length;
  }

  /** Get stream state */
  get state() {
    return super.state;
  }

  /**
   * Bind to request context
   * Called during res.sendStream()
   */
  _bind(context) {
    this.context = context;
  }
  registerControlHandler() {
    var _this$context3;
    if (!((_this$context3 = this.context) !== null && _this$context3 !== void 0 && _this$context3.registerStreamHandler)) return;
    this.context.registerStreamHandler(this.streamId, this.handleControlMessage.bind(this));
  }
  unregisterControlHandler() {
    var _this$context4;
    if (!((_this$context4 = this.context) !== null && _this$context4 !== void 0 && _this$context4.unregisterStreamHandler)) return;
    this.context.unregisterStreamHandler(this.streamId);
  }
  handleControlMessage(data) {
    // Update remote activity timestamp on any control message
    this.lastRemoteActivityAt = Date.now();
    switch (data.type) {
      case StreamInternalMessageType.PULL:
        {
          var credit = typeof data.credit === 'number' && data.credit > 0 ? data.credit : 1;
          this.pullCredit += credit;
          this.emit(StreamEvent.PULL, {
            credit,
            totalCredit: this.pullCredit
          });
          // Try flushing buffered chunks or pumping generator
          this.flush();
          break;
        }
      case StreamInternalMessageType.CANCEL:
        this.emit(StreamEvent.CANCEL, {
          reason: data.reason,
          remote: true
        });
        this.cancel(data.reason);
        break;
      default:
        break;
    }
  }
  clearIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
  performHeartbeat() {
    var _this = this;
    return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _this$context5;
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if ((_this$context5 = _this.context) !== null && _this$context5 !== void 0 && _this$context5.heartbeat) {
              _context.next = 1;
              break;
            }
            return _context.abrupt("return", false);
          case 1:
            if (!_this.heartbeatInFlight) {
              _this.heartbeatInFlight = Promise.resolve().then(() => _this.context.heartbeat()).catch(() => false).finally(() => {
                _this.heartbeatInFlight = null;
              });
            }
            return _context.abrupt("return", _this.heartbeatInFlight);
          case 2:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }))();
  }
  startIdleTimer() {
    var _this2 = this;
    if (!this.streamTimeout || this.streamTimeout <= 0) return;
    this.clearIdleTimer();
    var timeout = this.streamTimeout;
    this.idleTimer = setTimeout(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var ok;
      return _regeneratorRuntime.wrap(function (_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            if (!(_this2._state !== StreamStateConstant.STREAMING)) {
              _context2.next = 1;
              break;
            }
            return _context2.abrupt("return");
          case 1:
            if (!(Date.now() - _this2.lastRemoteActivityAt < timeout)) {
              _context2.next = 2;
              break;
            }
            _this2.startIdleTimer();
            return _context2.abrupt("return");
          case 2:
            _context2.next = 3;
            return _this2.performHeartbeat();
          case 3:
            ok = _context2.sent;
            if (!ok) {
              _context2.next = 4;
              break;
            }
            _this2.lastRemoteActivityAt = Date.now();
            _this2.startIdleTimer();
            return _context2.abrupt("return");
          case 4:
            try {
              _this2.emit(StreamEvent.TIMEOUT, {
                timeout
              });
              _this2.error(formatMessage(Messages.STREAM_TIMEOUT, timeout));
            } catch (_unused5) {
              /** ignore */
            }
          case 5:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    })), timeout);
  }

  /**
   * Send message (to client when server-side stream, to server when client-side stream)
   */
  sendMessage(type, data) {
    var _this$context$serverI;
    if (!this.context) {
      throw new RequestIframeStreamError({
        message: Messages.STREAM_NOT_BOUND,
        code: ErrorCode.STREAM_NOT_BOUND,
        streamId: this.streamId
      });
    }
    var isClientStream = this.context.clientId !== undefined && this.context.serverId === undefined;
    var role = isClientStream ? MessageRole.CLIENT : MessageRole.SERVER;
    var creatorId = (_this$context$serverI = this.context.serverId) !== null && _this$context$serverI !== void 0 ? _this$context$serverI : this.context.clientId;
    var message = createPostMessage(type, this.context.requestId, {
      secretKey: this.context.secretKey,
      body: _objectSpread({
        streamId: this.streamId
      }, data),
      role,
      creatorId,
      targetId: this.context.targetId
    });

    /**
     * Transferable optimization:
     * - If payload contains ArrayBuffer/TypedArray, transfer its underlying buffer to avoid copy.
     * - This helps large chunks (e.g. file streams) significantly.
     */
    var payloadData = data === null || data === void 0 ? void 0 : data.data;
    var transfer;
    try {
      if (typeof ArrayBuffer !== 'undefined' && payloadData instanceof ArrayBuffer) {
        transfer = [payloadData];
      } else if (typeof ArrayBuffer !== 'undefined' && typeof ArrayBuffer.isView === 'function' && payloadData && ArrayBuffer.isView(payloadData) && payloadData.buffer instanceof ArrayBuffer) {
        transfer = [payloadData.buffer];
      }
    } catch (_unused6) {
      /** ignore */
    }
    var ok = this.context.channel.send(this.context.targetWindow, message, this.context.targetOrigin, transfer);
    if (!ok) {
      this._state = StreamStateConstant.CANCELLED;
      this.clearExpireTimer();
      // For most stream messages, if we cannot send, treat as a hard cancellation signal
      // so callers can stop further processing immediately.
      throw new RequestIframeStreamError({
        message: formatMessage(Messages.STREAM_CANCELLED, Messages.TARGET_WINDOW_CLOSED),
        code: ErrorCode.TARGET_WINDOW_CLOSED,
        streamId: this.streamId,
        requestId: this.context.requestId
      });
    }
    return true;
  }
  ensureAckReceiver() {
    if (this.ackReceiverRegistered) return;
    if (!this.context) return;
    var ch = this.context.channel;
    if (!isFunction(ch.addReceiver) || !isFunction(ch.removeReceiver)) return;
    this.ackReceiver = (data, context) => {
      if (!data || data.type !== MessageType.ACK) return;
      var pending = this.ackWaiters.get(data.requestId);
      if (!pending) return;
      if (context && !context.handledBy) {
        context.markHandledBy(`stream:${this.streamId}`);
      }
      clearTimeout(pending.timeoutId);
      this.ackWaiters.delete(data.requestId);
      pending.resolve(true);
    };
    ch.addReceiver(this.ackReceiver);
    this.ackReceiverRegistered = true;
  }
  cleanupAckWaiters() {
    this.ackWaiters.forEach(p => {
      clearTimeout(p.timeoutId);
      p.resolve(false);
    });
    this.ackWaiters.clear();
    if (this.ackReceiverRegistered && this.ackReceiver && this.context) {
      var ch = this.context.channel;
      if (isFunction(ch.removeReceiver)) {
        try {
          ch.removeReceiver(this.ackReceiver);
        } catch (_unused7) {
          /** ignore */
        }
      }
    }
    this.ackReceiverRegistered = false;
    this.ackReceiver = undefined;
  }
  registerAckWaiter(requestId, timeoutMs, resolve) {
    var timeoutId = setTimeout(() => {
      this.ackWaiters.delete(requestId);
      resolve(false);
    }, timeoutMs);
    this.ackWaiters.set(requestId, {
      resolve,
      timeoutId
    });
  }
  clearExpireTimer() {
    if (this.expireTimer) {
      clearTimeout(this.expireTimer);
      this.expireTimer = null;
    }
  }
  startExpireTimer() {
    if (!this.expireTimeout || this.expireTimeout <= 0) return;
    var expireTimeout = this.expireTimeout;
    this.clearExpireTimer();
    this.expireTimer = setTimeout(() => {
      if (this._state !== StreamStateConstant.STREAMING) return;
      try {
        this.emit(StreamEvent.EXPIRED, {
          timeout: expireTimeout
        });
        this.error(formatMessage(Messages.STREAM_EXPIRED, expireTimeout));
      } catch (_unused8) {
        /** ignore timer-triggered send failures */
      }
    }, expireTimeout);
  }

  /**
   * Start stream transfer
   */
  start() {
    var _this3 = this;
    return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
      var _t;
      return _regeneratorRuntime.wrap(function (_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            if (_this3.context) {
              _context3.next = 1;
              break;
            }
            throw new RequestIframeStreamError({
              message: Messages.STREAM_NOT_BOUND,
              code: ErrorCode.STREAM_NOT_BOUND,
              streamId: _this3.streamId
            });
          case 1:
            if (!(_this3._state !== StreamStateConstant.PENDING)) {
              _context3.next = 2;
              break;
            }
            throw new RequestIframeStreamError({
              message: Messages.STREAM_ALREADY_STARTED,
              code: ErrorCode.STREAM_ALREADY_STARTED,
              streamId: _this3.streamId,
              requestId: _this3.context.requestId
            });
          case 2:
            _this3.completionPromise = new Promise((resolve, reject) => {
              _this3.resolveCompletion = resolve;
              _this3.rejectCompletion = reject;
            });
            _this3._state = StreamStateConstant.STREAMING;
            _this3.startExpireTimer();
            _this3.startIdleTimer();
            _this3.lastRemoteActivityAt = Date.now();
            _this3.registerControlHandler();

            // Send stream start message
            _this3.sendMessage(MessageType.STREAM_START, {
              type: _this3.type,
              mode: _this3.mode,
              chunked: _this3.chunked,
              metadata: _this3.metadata,
              autoResolve: _this3.autoResolve
            });
            _this3.emit(StreamEvent.START, {
              streamId: _this3.streamId,
              type: _this3.type,
              chunked: _this3.chunked,
              mode: _this3.mode,
              metadata: _this3.metadata
            });
            _context3.prev = 3;
            if (!(_this3.mode === StreamModeConstant.PUSH)) {
              _context3.next = 5;
              break;
            }
            _context3.next = 4;
            return _this3.completionPromise;
          case 4:
            return _context3.abrupt("return", _context3.sent);
          case 5:
            // pull protocol: produce only when receiver grants credit
            _this3.flush();
            _context3.next = 6;
            return _this3.completionPromise;
          case 6:
            return _context3.abrupt("return", _context3.sent);
          case 7:
            _context3.prev = 7;
            _t = _context3["catch"](3);
            if (!(_this3._state === StreamStateConstant.CANCELLED)) {
              _context3.next = 8;
              break;
            }
            _this3.clearExpireTimer();
            throw _t;
          case 8:
            _this3.error(_t.message || String(_t));
            _context3.next = 9;
            return _this3.completionPromise;
          case 9:
            return _context3.abrupt("return", _context3.sent);
          case 10:
          case "end":
            return _context3.stop();
        }
      }, _callee3, null, [[3, 7]]);
    }))();
  }

  /**
   * Push a chunk manually (mode === 'push').
   */

  write(data, doneOrOptions = false, options) {
    var _opts$ackTimeout;
    if (this.mode !== StreamModeConstant.PUSH) {
      var _this$context6;
      throw new RequestIframeStreamError({
        message: Messages.STREAM_WRITE_ONLY_IN_PUSH_MODE,
        code: ErrorCode.STREAM_WRITE_ONLY_IN_PUSH_MODE,
        streamId: this.streamId,
        requestId: (_this$context6 = this.context) === null || _this$context6 === void 0 ? void 0 : _this$context6.requestId
      });
    }
    if (this._state === StreamStateConstant.PENDING) {
      var _this$context7;
      /**
       * In push mode, users must call start() first so STREAM_START is sent and binding is complete.
       */
      throw new RequestIframeStreamError({
        message: Messages.STREAM_NOT_BOUND,
        code: ErrorCode.STREAM_NOT_BOUND,
        streamId: this.streamId,
        requestId: (_this$context7 = this.context) === null || _this$context7 === void 0 ? void 0 : _this$context7.requestId
      });
    }
    if (this._state !== StreamStateConstant.STREAMING) {
      var _this$context8;
      throw new RequestIframeStreamError({
        message: Messages.STREAM_ENDED,
        code: ErrorCode.STREAM_ENDED,
        streamId: this.streamId,
        requestId: (_this$context8 = this.context) === null || _this$context8 === void 0 ? void 0 : _this$context8.requestId
      });
    }
    var done = typeof doneOrOptions === 'boolean' ? doneOrOptions : false;
    var opts = typeof doneOrOptions === 'object' ? doneOrOptions : options;
    var requireAck = (opts === null || opts === void 0 ? void 0 : opts.requireAck) === true;
    var ackTimeout = (_opts$ackTimeout = opts === null || opts === void 0 ? void 0 : opts.ackTimeout) !== null && _opts$ackTimeout !== void 0 ? _opts$ackTimeout : DefaultTimeout.ACK;
    if (!requireAck) {
      // push mode now buffers and sends based on pull credit
      this.enqueue({
        data,
        done
      });
      this.emit(StreamEvent.WRITE, {
        data,
        done
      });
      this.flush();
      return;
    }
    return new Promise(resolve => {
      var ackRequestId = generateRequestId();
      this.enqueue({
        data,
        done,
        requireAck: true,
        ackRequestId,
        ackTimeout,
        resolveAck: resolve
      });
      this.emit(StreamEvent.WRITE, {
        data,
        done
      });
      this.flush();
    });
  }

  /**
   * End the stream (mode === 'push').
   */
  end() {
    if (this.mode !== StreamModeConstant.PUSH) {
      // For pull mode, end is controlled internally
      this.endInternal();
      return;
    }
    // In push mode, end means enqueue a terminal marker if nothing queued
    if (this.mode === StreamModeConstant.PUSH) {
      if (this.pendingQueue.length === 0) {
        this.enqueue({
          data: undefined,
          done: true
        });
      } else {
        // Ensure the last queued chunk marks done
        this.pendingQueue[this.pendingQueue.length - 1].done = true;
      }
      this.flush();
      return;
    }
    this.endInternal();
  }

  /**
   * Generate data from iterator
   */
  pumpFromGenerator() {
    var _this4 = this;
    return _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
      var gen, r, result, _t2;
      return _regeneratorRuntime.wrap(function (_context4) {
        while (1) switch (_context4.prev = _context4.next) {
          case 0:
            if (!_this4.pumping) {
              _context4.next = 1;
              break;
            }
            return _context4.abrupt("return");
          case 1:
            _this4.pumping = true;
            _context4.prev = 2;
            if (!_this4.iterator) {
              _context4.next = 8;
              break;
            }
            gen = _this4.iterator();
          case 3:
            if (!(_this4._state === StreamStateConstant.STREAMING)) {
              _context4.next = 7;
              break;
            }
            if (!(_this4.pullCredit <= 0)) {
              _context4.next = 4;
              break;
            }
            return _context4.abrupt("continue", 7);
          case 4:
            _context4.next = 5;
            return gen.next();
          case 5:
            r = _context4.sent;
            if (!r.done) {
              _context4.next = 6;
              break;
            }
            // no more data: send end
            _this4.endInternal();
            return _context4.abrupt("continue", 7);
          case 6:
            _this4.enqueue({
              data: r.value,
              done: false
            });
            _this4.flush();
            _context4.next = 3;
            break;
          case 7:
            _context4.next = 15;
            break;
          case 8:
            if (!_this4.nextFn) {
              _context4.next = 14;
              break;
            }
          case 9:
            if (!(_this4._state === StreamStateConstant.STREAMING)) {
              _context4.next = 13;
              break;
            }
            if (!(_this4.pullCredit <= 0)) {
              _context4.next = 10;
              break;
            }
            return _context4.abrupt("continue", 13);
          case 10:
            _context4.next = 11;
            return Promise.resolve(_this4.nextFn());
          case 11:
            result = _context4.sent;
            _this4.enqueue({
              data: result.data,
              done: result.done
            });
            _this4.flush();
            if (!result.done) {
              _context4.next = 12;
              break;
            }
            return _context4.abrupt("continue", 13);
          case 12:
            _context4.next = 9;
            break;
          case 13:
            _context4.next = 15;
            break;
          case 14:
            // No producer, just end when pulled
            if (_this4.pullCredit > 0) {
              _this4.endInternal();
            }
          case 15:
            _context4.next = 17;
            break;
          case 16:
            _context4.prev = 16;
            _t2 = _context4["catch"](2);
            if (_this4._state === StreamStateConstant.STREAMING) {
              _this4.error((_t2 === null || _t2 === void 0 ? void 0 : _t2.message) || String(_t2));
            }
          case 17:
            _context4.prev = 17;
            _this4.pumping = false;
            return _context4.finish(17);
          case 18:
          case "end":
            return _context4.stop();
        }
      }, _callee4, null, [[2, 16, 17, 18]]);
    }))();
  }

  /**
   * Send data chunk
   */
  sendData(data, done = false, options) {
    var _this$context$serverI2, _options$requestId;
    if (!this.context) {
      throw new RequestIframeStreamError({
        message: Messages.STREAM_NOT_BOUND,
        code: ErrorCode.STREAM_NOT_BOUND,
        streamId: this.streamId
      });
    }
    var seq = this.seq++;
    var isClientStream = this.context.clientId !== undefined && this.context.serverId === undefined;
    var role = isClientStream ? MessageRole.CLIENT : MessageRole.SERVER;
    var creatorId = (_this$context$serverI2 = this.context.serverId) !== null && _this$context$serverI2 !== void 0 ? _this$context$serverI2 : this.context.clientId;
    var message = createPostMessage(MessageType.STREAM_DATA, (_options$requestId = options === null || options === void 0 ? void 0 : options.requestId) !== null && _options$requestId !== void 0 ? _options$requestId : this.context.requestId, {
      secretKey: this.context.secretKey,
      requireAck: options === null || options === void 0 ? void 0 : options.requireAck,
      /**
       * When per-frame requireAck is enabled, include a unique identifier in ack.
       * - seq is the stream frame sequence number.
       *
       * NOTE: ack is an internal reserved field (not part of public API).
       */
      ack: options !== null && options !== void 0 && options.requireAck ? {
        id: `${this.streamId}:${seq}`
      } : undefined,
      body: {
        streamId: this.streamId,
        data: this.encodeData(data),
        done,
        seq
      },
      role,
      creatorId,
      targetId: this.context.targetId
    });
    var ok = this.context.channel.send(this.context.targetWindow, message, this.context.targetOrigin);
    if (!ok) {
      this._state = StreamStateConstant.CANCELLED;
      this.clearExpireTimer();
      throw new RequestIframeStreamError({
        message: formatMessage(Messages.STREAM_CANCELLED, Messages.TARGET_WINDOW_CLOSED),
        code: ErrorCode.TARGET_WINDOW_CLOSED,
        streamId: this.streamId,
        requestId: this.context.requestId
      });
    }
    this.emit(StreamEvent.SEND, {
      seq,
      done
    });
  }
  flush() {
    if (this._state !== StreamStateConstant.STREAMING) return;

    // First try to pump from generator if needed
    if (this.mode === StreamModeConstant.PULL && this.pendingQueue.length === 0) {
      void this.pumpFromGenerator();
    }
    while (this.pullCredit > 0 && this.pendingQueue.length > 0 && this._state === StreamStateConstant.STREAMING) {
      var item = this.pendingQueue.shift();
      this.pendingBytes -= item.bytes;
      this.pullCredit--;
      try {
        if (item.requireAck && item.ackRequestId && item.resolveAck) {
          this.ensureAckReceiver();
          if (this.ackReceiverRegistered) {
            var _item$ackTimeout;
            this.registerAckWaiter(item.ackRequestId, (_item$ackTimeout = item.ackTimeout) !== null && _item$ackTimeout !== void 0 ? _item$ackTimeout : DefaultTimeout.ACK, item.resolveAck);
            this.sendData(item.data, item.done, {
              requestId: item.ackRequestId,
              requireAck: true
            });
          } else {
            item.resolveAck(false);
            this.sendData(item.data, item.done);
          }
        } else {
          this.sendData(item.data, item.done);
        }
      } catch (e) {
        var _this$rejectCompletio, _this$context9;
        // send failure treated as cancellation
        this._state = StreamStateConstant.CANCELLED;
        this.clearExpireTimer();
        this.clearIdleTimer();
        this.unregisterControlHandler();
        this.pendingQueue.length = 0;
        this.pendingBytes = 0;
        this.cleanupAckWaiters();
        (_this$rejectCompletio = this.rejectCompletion) === null || _this$rejectCompletio === void 0 || _this$rejectCompletio.call(this, e instanceof Error ? e : new RequestIframeStreamError({
          message: String(e),
          code: ErrorCode.STREAM_ERROR,
          streamId: this.streamId,
          requestId: (_this$context9 = this.context) === null || _this$context9 === void 0 ? void 0 : _this$context9.requestId,
          cause: e
        }));
        throw e;
      }
      if (item.done) {
        this.endInternal();
        break;
      }
    }
  }

  /**
   * Encode data (subclasses can override, e.g., FileStream needs Base64 encoding)
   */
  encodeData(data) {
    return data;
  }

  /**
   * End stream
   */
  endInternal() {
    var _this$resolveCompleti;
    if (this._state !== StreamStateConstant.STREAMING) return;
    this._state = StreamStateConstant.ENDED;
    this.clearExpireTimer();
    this.clearIdleTimer();
    this.unregisterControlHandler();
    this.pendingQueue.length = 0;
    this.pendingBytes = 0;
    this.sendMessage(MessageType.STREAM_END);
    this.emit(StreamEvent.END);
    this.emit(StreamEvent.STATE, {
      state: this._state
    });
    this.cleanupAckWaiters();
    this.clearAllListeners();
    (_this$resolveCompleti = this.resolveCompletion) === null || _this$resolveCompleti === void 0 || _this$resolveCompleti.call(this);
  }

  /**
   * Send error
   */
  error(message) {
    var _this$context0, _this$resolveCompleti2;
    if (this._state !== StreamStateConstant.STREAMING) return;
    this._state = StreamStateConstant.ERROR;
    this.clearExpireTimer();
    this.clearIdleTimer();
    this.unregisterControlHandler();
    this.pendingQueue.length = 0;
    this.pendingBytes = 0;
    this.sendMessage(MessageType.STREAM_ERROR, {
      error: message
    });
    this.emit(StreamEvent.ERROR, {
      error: new RequestIframeStreamError({
        message,
        code: ErrorCode.STREAM_ERROR,
        streamId: this.streamId,
        requestId: (_this$context0 = this.context) === null || _this$context0 === void 0 ? void 0 : _this$context0.requestId
      })
    });
    this.emit(StreamEvent.STATE, {
      state: this._state
    });
    this.cleanupAckWaiters();
    this.clearAllListeners();
    (_this$resolveCompleti2 = this.resolveCompletion) === null || _this$resolveCompleti2 === void 0 || _this$resolveCompleti2.call(this);
  }

  /**
   * Abort stream transfer (is alias of cancel method)
   */
  abort(reason) {
    this.cancel(reason);
  }

  /**
   * Cancel stream transfer
   */
  cancel(reason) {
    var _this$resolveCompleti3;
    if (this._state !== StreamStateConstant.PENDING && this._state !== StreamStateConstant.STREAMING) return;
    this._state = StreamStateConstant.CANCELLED;
    this.clearExpireTimer();
    this.clearIdleTimer();
    this.unregisterControlHandler();
    this.pendingQueue.length = 0;
    this.pendingBytes = 0;
    this.emit(StreamEvent.CANCEL, {
      reason,
      remote: false
    });
    this.emit(StreamEvent.STATE, {
      state: this._state
    });
    if (this.context) {
      try {
        this.sendMessage(MessageType.STREAM_CANCEL, {
          reason
        });
      } catch (_unused9) {
        // ignore send failures on cancel
      }
    }
    this.cleanupAckWaiters();
    this.clearAllListeners();
    (_this$resolveCompleti3 = this.resolveCompletion) === null || _this$resolveCompleti3 === void 0 || _this$resolveCompleti3.call(this);
  }
}